/**
 * 撮影後の処理（ビューファインダー → 処理中 → 測光）
 */
import { showScreen, toast, setLoading } from './ui.js';
import { computeWeightsFromPreset, blobToMonoBlob } from './mono.js';
import { initShootFromCapture, flushSave, updateCurrentShoot } from './scene.js';
import { getAspectRatio } from './presets.js';
import { calculateCenterCrop, cropToBlob, blobToImage } from './crop.js';
import { getFilmById } from './data/films.js';
import { FILTER_FACTORS } from './presets.js';

/**
 * 撮影完了 → モノクロ生成 → シーン作成（下書きとして即保存）→ 測光へ
 */
export async function startShootFromCapture(preset, originalBlob, croppedBlob, meta) {
  const film = getFilmById(preset.filmId);
  const filter = FILTER_FACTORS[preset.filterId];
  setLoading('モノクロ変換しています', `${(film?.name || preset.filmId).toUpperCase()}${filter && preset.filterId ? ` + ${filter.name.toUpperCase()}` : ''}`);
  try {
    const weights = computeWeightsFromPreset(preset);
    const mono = await blobToMonoBlob(croppedBlob, weights, 1600);
    initShootFromCapture({
      preset,
      originalBlob,
      croppedBlob,
      monoBlob: mono.blob,
      cropBox: meta.cropBox,
      meta: { orientation: meta.orientation, sourceW: meta.sourceW, sourceH: meta.sourceH },
      gps: meta.gps,
      capturedAt: meta.capturedAt,
    });
    if (meta.source) updateCurrentShoot({ source: meta.source }); // 'photo-library' | 'no-photo'
    await flushSave().catch(() => {});
    // 天候/月齢/日の出を非同期で取得（撮影の流れは止めない）
    if (meta.gps) {
      import('./environment.js').then((m) => m.fetchEnvironment(meta.gps, meta.capturedAt));
    }
    const m = await import('./mapping.js');
    await m.openMapping({ fresh: true });
  } catch (err) {
    console.error('[shoot] failed:', err);
    toast('処理に失敗しました');
    showScreen('select-gear', { back: true });
  }
}


// ====================================================================
// 15. 写真アプリから読み込む／写真なしで記録
// ====================================================================

/** 写真ピッカーを開いて 1 枚読み込み、プリセットの判型で中央からクロップして測光へ */
export function importFromPhotos(preset) {
  const input = document.createElement('input');
  input.type = 'file';
  input.accept = 'image/*';
  input.style.display = 'none';
  document.body.appendChild(input);
  input.addEventListener('change', async () => {
    const file = input.files?.[0];
    input.remove();
    if (!file) return;
    if (!/^image\//.test(file.type || 'image/')) { toast('画像ファイルを選んでください'); return; }
    if (file.size > 50 * 1024 * 1024) { toast('画像が大きすぎます（50MB まで）'); return; }
    setLoading('画像を読み込んでいます');
    try {
      const img = await blobToImage(file);
      const W = img.naturalWidth;
      const H = img.naturalHeight;
      // 向きはプリセットの指定、自動なら写真の縦横に合わせる
      const orientation = preset.defaultOrientation === 'portrait' || preset.defaultOrientation === 'landscape'
        ? preset.defaultOrientation : (H > W ? 'portrait' : 'landscape');
      const originalBlob = await cropToBlob(img, { x: 0, y: 0, w: W, h: H }, W, H, 2400, 0.86);
      const cropBox = calculateCenterCrop(W, H, getAspectRatio(preset, orientation));
      const croppedBlob = await cropToBlob(img, cropBox, W, H);
      const lm = Number(file.lastModified);
      await startShootFromCapture(preset, originalBlob, croppedBlob, {
        orientation, sourceW: W, sourceH: H, cropBox, gps: null,
        // 写真の日時が分かればそれを撮影日時にする（未来・0 は使わない）
        capturedAt: lm > 0 && lm <= Date.now() ? lm : Date.now(),
        source: 'photo-library',
      });
    } catch (e) {
      console.error('[import] failed:', e);
      toast('画像を読み込めませんでした（HEIC などは JPEG で保存し直してください）');
      showScreen('select-gear', { back: true });
    }
  });
  input.click();
}

/** 写真なしで記録：判型の比率の無地の画面を用意して、その上にピンを置く */
export async function recordWithoutPhoto(preset) {
  const orientation = preset.defaultOrientation === 'landscape' ? 'landscape' : 'portrait';
  const [aw, ah] = getAspectRatio(preset, orientation);
  const longSide = 1600;
  const w = aw >= ah ? longSide : Math.round((longSide * aw) / ah);
  const h = aw >= ah ? Math.round((longSide * ah) / aw) : longSide;
  const c = document.createElement('canvas');
  c.width = w;
  c.height = h;
  const g = c.getContext('2d');
  g.fillStyle = '#9a9a9a';
  g.fillRect(0, 0, w, h);
  // 位置の目安になる三分割線
  g.strokeStyle = 'rgba(255,255,255,0.35)';
  g.lineWidth = 2;
  [1, 2].forEach((k) => {
    g.beginPath(); g.moveTo((w * k) / 3, 0); g.lineTo((w * k) / 3, h); g.stroke();
    g.beginPath(); g.moveTo(0, (h * k) / 3); g.lineTo(w, (h * k) / 3); g.stroke();
  });
  const blob = await new Promise((r) => c.toBlob(r, 'image/jpeg', 0.9));
  await startShootFromCapture(preset, blob, blob, {
    orientation, sourceW: w, sourceH: h, cropBox: { x: 0, y: 0, w, h }, gps: null, capturedAt: Date.now(), source: 'no-photo',
  });
}
